import { Divider, Grid, Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CommonAccordion from '@src/components/Common/CommonAccordion'
import CommonTabs, { CommonTab } from '@src/components/Common/CommonTabs'
import { checkModErrorsAndScroll } from '@src/routes/Configurators/products/TubesMTORedesign/utils'
import { useUserSession } from '@utils/useUserSession'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  baseLengthValue,
  getBaseLength,
  InsertModificationArgs,
  shouldExpandCommonAccordion,
  useProductFromQueryParam,
  SequenceType,
  getProductBasedBaseType,
} from '../../ProductsConfiguration'
import useFormikHelper from '../../useFormikHelper'
import BaseModifications from './BaseModifications'
import FiveAndThreeModifications from './FiveAndThreeModifications'
import FiveOrThreeModifications from './FiveOrThreeModifications'
import InternalModifications from './InternalModifications'
import { ModificationTypes, TubeProduct } from '../../enum'
import InternalModificationsForLongOligos from './InternalModificationsForLongOligos'
import FiveReporterAndThreeQuencher from './FiveReporterAndThreeQuencher'
import ModificationsAccordionSummary from './ModificationsAccordionSummary'
import { BiTubeProduct } from '../../../BiTube/enum'

const useStyle = makeStyles((theme: Theme) => {
  return {
    summary: {
      backgroundColor: theme.palette.primary.main,
      borderTopLeftRadius: theme.typography.pxToRem(5),
      borderTopRightRadius: theme.typography.pxToRem(5),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0, 4),
      width: '100%',
    },
    details: {
      padding: 0,
    },
  }
})

interface ModificationsAccordionProps {
  insertModificationIntoSequence: ({
    value,
    resetMod,
    isAntiSense,
  }: InsertModificationArgs) => Promise<string>
  groupedModifications: object
  sequenceType?: SequenceType
}

const ModificationsAccordion: React.FC<ModificationsAccordionProps> = ({
  insertModificationIntoSequence,
  groupedModifications,
  sequenceType = SequenceType.Primary,
}) => {
  const intl = useIntl()
  const classes = useStyle()
  const { userSession } = useUserSession()
  const formik = useFormikHelper()

  const product = useProductFromQueryParam()
  const { Rna, Wellred, LongOligo, Probe, Beacon } = TubeProduct
  const { Duplex, mirna } = BiTubeProduct
  const isMiRnaProduct = product === mirna

  const disableAntiSense =
    sequenceType === SequenceType.Secondary &&
    (isMiRnaProduct ? !formik.isValidAntiSense() : !formik.isValidSense())

  const baseLength = getBaseLength(
    product,
    userSession.country,
    formik.selectedOligoValues.probeType
  )
  const overHang =
    sequenceType === SequenceType.Primary
      ? formik.selectedOligoValues?.customerOverhang === '[None]'
        ? 0
        : baseLength.overHang || 0
      : formik.selectedOligoValues?.customerAntisenseOverHang === '[None]'
        ? 0
        : baseLength.overHang || 0
  const maxBaseLength = baseLength.max - overHang
  const expandCommonAccordion =
    shouldExpandCommonAccordion(product) ||
    (isMiRnaProduct && sequenceType === SequenceType.Primary)
  const [expanded, setExpanded] = useState<boolean>(expandCommonAccordion)

  const internalModDegenerateBasesDisabled =
    disableAntiSense ||
    (!formik.selectedOligoValues.isOverridden &&
      baseLengthValue(
        sequenceType === SequenceType.Primary
          ? formik.selectedOligoValues.sequence
          : formik.selectedOligoValues.antisenceSequence || ''
      ) > maxBaseLength) ||
    !!(
      sequenceType === SequenceType.Secondary &&
      formik.selectedOligoValues.autoGenerateOligo
    )
  const fiveThreeModDisabled =
    disableAntiSense ||
    !formik.selectedOligoValues.sequence ||
    formik.selectedOligoValues.sequence === ''
  const baseType = getProductBasedBaseType(product)
  const { FiveMod, ThreeMod, InternalMod, ReporterQuencher } = ModificationTypes
  const [fiveModData] = groupedModifications?.[FiveMod] || []
  const [threeModData] = groupedModifications?.[ThreeMod] || []
  const [internalModData] = groupedModifications?.[InternalMod] || []
  const [baseModifications] = groupedModifications[baseType] || []
  const [reporterQuencherData] = groupedModifications[ReporterQuencher] || []
  const probeType = formik.selectedOligoValues.probeType
  const categoryNameToFind =
    !probeType || probeType === 'duallabel' ? 'default' : probeType

  const probeModData = reporterQuencherData?.category.find(
    (cat) => cat.categoryName === categoryNameToFind
  )

  const isDuplexProduct = product === Duplex
  const fiveAndThreeModTab = {
    label: intl.formatMessage({
      id: 'FIVE_AND_THREE_MODIFICATIONS',
      defaultMessage: "5 & 3' Modifications",
    }),
    component: (
      <FiveAndThreeModifications
        disabled={fiveThreeModDisabled}
        fiveModData={fiveModData}
        threeModData={threeModData}
      />
    ),
    id: 'five-and-three-mod-tab',
  }

  const internalModificationsTab = {
    label: intl.formatMessage({
      id: '_PC_SPEC_INTERNALMODS',
      defaultMessage: 'Internal Modifications',
    }),
    component: (
      <InternalModifications
        disabled={internalModDegenerateBasesDisabled}
        internalModData={internalModData}
        insertModificationIntoSequence={insertModificationIntoSequence}
      />
    ),
    id: 'internal-mod-tab',
  }

  let baseModificationLabel = intl.formatMessage({
    id: 'CONF_BASES',
    defaultMessage: 'Bases',
  })

  if (expanded && !isDuplexProduct) {
    baseModificationLabel =
      baseModificationLabel +
      ' ' +
      intl.formatMessage({
        id: 'CONF_OPTIONAL',
        defaultMessage: '(Optional)',
      })
  }
  const baseModificationsTab = {
    label: baseModificationLabel,
    component: (
      <BaseModifications
        disabled={internalModDegenerateBasesDisabled}
        degenerateBasesData={baseModifications}
        insertModificationIntoSequence={insertModificationIntoSequence}
        isLNABaseEnable={!!formik.selectedOligoValues.isLNABaseEnable}
        isAntiSense={
          sequenceType === SequenceType.Secondary ||
          (sequenceType === SequenceType.Primary && isMiRnaProduct)
        }
      />
    ),
    id: 'base-mod-tab',
  }

  const fiveModRequired =
    isMiRnaProduct && sequenceType === SequenceType.Primary

  const fiveModificationsTab = {
    label: `${fiveModRequired ? '*' : ''}${intl.formatMessage({
      id: 'CONF_5_MOD_standard',
      defaultMessage: "5' Modifications",
    })}`,
    component: (
      <FiveOrThreeModifications
        disabled={fiveThreeModDisabled}
        modData={fiveModData}
        modificationType={
          sequenceType === SequenceType.Primary ? 'fiveMod' : 'antisenceFiveMod'
        }
      />
    ),
    id: 'five-mod-tab',
  }

  const internalModificationsForLongOligosForLongOligsTab = {
    label: intl.formatMessage({
      id: '_PC_SPEC_INTERNALMODS',
      defaultMessage: 'Internal Modifications',
    }),
    component: (
      <InternalModificationsForLongOligos
        disabled={!!disableAntiSense}
        degenerateBasesData={internalModData}
        insertModificationIntoSequence={insertModificationIntoSequence}
      />
    ),
    id: 'internal-mod-tab',
  }

  const threeModificationsTab = {
    label: intl.formatMessage({
      id: 'CONF_3_MOD_standard',
      defaultMessage: "3' Modifications",
    }),
    component: (
      <FiveOrThreeModifications
        disabled={fiveThreeModDisabled}
        modData={threeModData}
        modificationType={
          sequenceType === SequenceType.Primary
            ? 'threeMod'
            : 'antisenceThreeMod'
        }
      />
    ),
    id: 'three-mod-tab',
  }

  let fiveReporterAndThreeQuencherLabel
  if (product === Beacon) {
    fiveReporterAndThreeQuencherLabel = intl.formatMessage({
      id: 'REPORTER_QUENCHER_TEXT',
      defaultMessage: "5' Reporter / 3' Quencher Combination",
    })
  } else if (product === Probe) {
    fiveReporterAndThreeQuencherLabel = intl.formatMessage({
      id:
        probeModData?.categoryDisplayName || '_PC_SPEC_REPORTER_QUENCHER_TEXT',
      defaultMessage: "5' Reporter / 3' Quencher Combination",
    })
  }
  // We have same key as _PC_SPEC_REPORTER_QUENCHER_TEXT in /src/routes/Configurators/messages.ts
  const fiveReporterAndThreeQuencher = {
    label: '*' + fiveReporterAndThreeQuencherLabel,
    component: (
      <FiveReporterAndThreeQuencher
        disabled={fiveThreeModDisabled}
        modData={
          product === Probe ? probeModData : reporterQuencherData?.category[0]
        }
        modificationType="five-reporter-three-quencher"
        maxBaseLength={maxBaseLength}
      />
    ),
    id: 'five-reporter-three-quencher-tab',
  }

  const tabs: CommonTab[] = []

  if (product === Rna) {
    tabs.push(fiveModificationsTab, threeModificationsTab, baseModificationsTab)
  } else if (product === Wellred) {
    tabs.push(fiveModificationsTab)
  } else if (product === LongOligo) {
    tabs.push(
      fiveModificationsTab,
      internalModificationsForLongOligosForLongOligsTab,
      threeModificationsTab
    )
  } else if (product === Probe) {
    tabs.push(fiveReporterAndThreeQuencher, baseModificationsTab)
  } else if (product === Beacon) {
    tabs.push(fiveReporterAndThreeQuencher, baseModificationsTab)
  } else if (product === Duplex) {
    tabs.push(fiveModificationsTab, threeModificationsTab, baseModificationsTab)
  } else if (product === mirna && sequenceType === SequenceType.Secondary) {
    tabs.push(threeModificationsTab, baseModificationsTab)
  } else if (product === mirna && sequenceType === SequenceType.Primary) {
    tabs.push(fiveModificationsTab, threeModificationsTab)
  } else {
    tabs.push(
      fiveAndThreeModTab,
      internalModificationsTab,
      baseModificationsTab
    )
  }

  useEffect(() => {
    formik.selectedOligoValues.displayPageError &&
      formik.errors &&
      checkModErrorsAndScroll({
        validationObj: formik.errors,
        callback: setExpanded,
      })
    const errorFields =
      formik.selectedOligoValues.displayPageError &&
      formik.errors &&
      Object.entries(formik.errors).filter(
        ([key, value]) => key && value !== ''
      )
    const hasOnlyModErrors =
      errorFields &&
      errorFields.every(([key]) => key === ThreeMod || key === FiveMod)
    if (hasOnlyModErrors) {
      formik.setFieldValue('activeTab', 0)
    }
  }, [formik.errors])

  useEffect(() => {
    if (
      formik.selectedOligoValues.sequence === '' &&
      internalModData &&
      formik.selectedOligoValues.enabledInternalModifications?.length === 0
    ) {
      const enabled = internalModData.category.flatMap((cat) => {
        return cat.modifications.map((mod) => mod.value)
      })
      formik.setOligoFieldValue('enabledInternalModifications', enabled, false)
    }
  }, [
    groupedModifications,
    formik.allValues.oligoList[0],
    formik.activeOligoIndex,
  ])

  return (
    <CommonAccordion
      expanded={expanded}
      summary={
        <ModificationsAccordionSummary
          summary={
            <>
              <FormattedMessage
                id="CONF_ADD_MODIFICATIONS"
                defaultMessage="Add Modifications"
              />{' '}
              {!expandCommonAccordion &&
                !(isMiRnaProduct && sequenceType === SequenceType.Primary) && (
                  <FormattedMessage
                    id="CONF_OPTIONAL"
                    defaultMessage="(Optional)"
                  />
                )}
            </>
          }
          expanded={expanded}
        />
      }
      summaryId="modifications-panel-header"
      summaryAriaControls="modifications-panel-content"
      details={
        <Grid item xs={12}>
          <CommonTabs
            tabs={tabs}
            activeTabIndex={
              (isMiRnaProduct || isDuplexProduct) &&
              sequenceType === SequenceType.Secondary
                ? formik.allValues.antisenseActiveTab
                : formik.allValues.activeTab
            }
            changeCallback={(newValue) =>
              formik.setFieldValue(
                `${(isDuplexProduct || isMiRnaProduct) && sequenceType === SequenceType.Secondary ? 'antisenseActiveTab' : 'activeTab'}`,
                newValue
              )
            }
          />
          <Divider style={{ marginTop: '24px' }} />
        </Grid>
      }
      expandedCallback={(isExpanded: boolean) => setExpanded(isExpanded)}
      overrideClasses={{
        summary: classes.summary,
        details: classes.details,
      }}
    />
  )
}

export default ModificationsAccordion
